<template>
  <v-dialog v-model="visible" max-width="1200px" persistent scrollable>
    <v-card>
      <v-toolbar dense flat :color="esEdicion ? 'orange darken-2' : 'primary'" dark>
        <v-toolbar-title class="text-subtitle-1 font-weight-bold">
          <v-icon left small>{{ esEdicion ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
          {{ esEdicion ? 'Editar' : 'Registrar' }} Lista de Precio
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon small dark @click="cerrar" :disabled="enviando">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4" style="max-height: 70vh">
        <v-form ref="formulario" @submit.prevent="guardar">
          <!-- Configuración general -->
          <v-sheet rounded outlined class="pa-3 mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon small color="grey darken-1" class="mr-1">mdi-tune</v-icon>
              <span class="text-caption font-weight-bold grey--text text--darken-2">CONFIGURACIÓN GENERAL</span>
            </div>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.tipoListaPrecioId"
                  :items="tiposListaPrecioConEtiqueta"
                  item-text="etiqueta"
                  item-value="id"
                  label="Tipo Lista Precio"
                  outlined
                  dense
                  :loading="cargandoSelects"
                  :rules="[(v) => !!v || 'El tipo es requerido']"
                  :error-messages="erroresServidor.tipoListaPrecioId"
                  @change="limpiarErrorServidor('tipoListaPrecioId')">
                  <template v-slot:prepend-inner>
                    <v-icon small class="mt-1">mdi-format-list-bulleted-type</v-icon>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.categoriaUniversidadId"
                  :items="categoriasUniversidad"
                  item-text="nombre"
                  item-value="id"
                  label="Categoría"
                  outlined
                  dense
                  :loading="cargandoSelects"
                  :rules="[(v) => !!v || 'La categoría es requerida']"
                  :error-messages="erroresServidor.categoriaUniversidadId"
                  @change="limpiarErrorServidor('categoriaUniversidadId')">
                  <template v-slot:prepend-inner>
                    <v-icon small class="mt-1">mdi-shape</v-icon>
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </v-sheet>

          <!-- Áreas -->
          <div class="d-flex align-center mb-3">
            <v-icon small color="grey darken-1" class="mr-1">mdi-map-marker-multiple</v-icon>
            <span class="text-subtitle-2 font-weight-bold">Áreas</span>
            <v-chip x-small label class="ml-2" color="grey lighten-2">{{ form.areas.length }}</v-chip>
            <v-spacer />
            <v-btn small color="primary" @click="agregarArea">
              <v-icon left small>mdi-plus</v-icon>
              Agregar Área
            </v-btn>
          </div>

          <!-- Card por cada área -->
          <v-card v-for="(area, aIdx) in form.areas" :key="aIdx" outlined class="mb-4">
            <v-card-title
              class="py-2 px-3 text-subtitle-2 d-flex align-center"
              :class="aIdx % 2 === 0 ? 'grey lighten-4' : 'blue-grey lighten-5'">
              <v-icon small class="mr-2">mdi-map-marker</v-icon>
              Área #{{ aIdx + 1 }}
              <v-spacer />
              <v-btn icon x-small color="red darken-2" @click="eliminarArea(aIdx)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-divider />

            <v-card-text class="pa-3">
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="area.areaUniversidadId"
                    :items="areasUniversidad"
                    item-text="nombre"
                    item-value="id"
                    label="Área Universidad"
                    outlined
                    dense
                    :rules="[(v) => !!v || 'El área es requerida']"
                    :error-messages="obtenerErroresArea(aIdx, 'areaUniversidadId')"
                    @change="onAreaChange(area)">
                    <template v-slot:prepend-inner>
                      <v-icon small class="mt-1">mdi-map-marker</v-icon>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="area.modalidad"
                    :items="modalidades"
                    label="Modalidad"
                    outlined
                    dense
                    :disabled="!areaRequiereModalidad(area)"
                    :rules="[(v) => !!v || 'La modalidad es requerida']">
                    <template v-slot:selection="{ item }">
                      <v-chip x-small label :color="item === 'teorico' ? 'indigo lighten-4' : 'teal lighten-4'">{{
                        item
                      }}</v-chip>
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <!-- Conceptos del área -->
              <div class="d-flex align-center mb-2 mt-1">
                <v-icon x-small color="grey darken-1" class="mr-1">mdi-tag-multiple</v-icon>
                <span class="text-caption font-weight-bold grey--text text--darken-2">CONCEPTOS</span>
                <v-spacer />
                <v-btn x-small color="primary" outlined @click="agregarConcepto(area)">
                  <v-icon x-small left>mdi-plus</v-icon>
                  Agregar Concepto
                </v-btn>
              </div>

              <v-alert v-if="area.conceptos.length === 0" type="info" dense text class="mb-2">
                Sin conceptos. Haga clic en "Agregar Concepto".
              </v-alert>

              <!-- Card por cada concepto -->
              <v-card v-for="(concepto, cIdx) in area.conceptos" :key="cIdx" outlined class="mb-3 pa-3">
                <div class="d-flex align-center mb-2">
                  <span class="text-caption font-weight-bold grey--text">Concepto #{{ cIdx + 1 }}</span>
                  <v-spacer />
                  <v-btn icon x-small color="red" @click="eliminarConcepto(area, cIdx)">
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>

                <v-row dense>
                  <v-col cols="12" md="3">
                    <!-- <v-select
                      v-model="concepto.conceptoPagoId"
                      :items="conceptosPagoFiltrados(area.conceptos, cIdx)"
                      item-text="nombre"
                      item-value="id"
                      label="Concepto Pago"
                      outlined
                      dense
                      :rules="[(v) => !!v || 'Requerido']" /> -->
                    <v-select
                      v-model="concepto.conceptoPagoId"
                      :items="conceptosPagoFiltrados(area.conceptos, cIdx)"
                      item-value="id"
                      label="Concepto Pago"
                      outlined
                      dense
                      :rules="[(v) => !!v || 'Requerido']">
                      <template #item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.nombre }}</v-list-item-title>
                          <v-list-item-subtitle>{{ item.unidadNegocio }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </template>

                      <template #selection="{ item }">
                        {{ item.nombre }}
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model.number="concepto.montoCuota"
                      label="Monto Cuota / Módulo"
                      outlined
                      dense
                      type="number"
                      min="0"
                      :rules="[(v) => v > 0 || 'Mayor a 0']" />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model.number="concepto.numeroCuotas"
                      label="N° Cuotas / Módulos"
                      outlined
                      dense
                      type="number"
                      min="1"
                      :rules="[(v) => v >= 1 || 'Mínimo 1']" />
                  </v-col>
                  <v-col cols="12" md="2" class="d-flex align-center">
                    <v-checkbox
                      v-model="concepto.admiteCuotasVariables"
                      label="Admite cuotas variables"
                      dense
                      hide-details
                      class="mt-0 pt-0" />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-sheet
                      rounded
                      class="pa-2 d-flex align-center justify-center primary lighten-5"
                      style="height: 40px">
                      <span class="text-caption grey--text text--darken-1 mr-1">TOTAL</span>
                      <span class="text-subtitle-2 font-weight-bold primary--text">
                        {{ calcularPrecioTotal(concepto) }}
                      </span>
                    </v-sheet>
                  </v-col>
                </v-row>

                <!-- Modalidades de pago del concepto -->
                <v-row dense>
                  <!-- Contado -->
                  <v-col cols="12" md="6">
                    <v-card outlined class="pa-2" :style="{ borderLeft: '3px solid #388E3C' }">
                      <div class="d-flex align-center mb-2">
                        <v-icon x-small color="green darken-2" class="mr-1">mdi-cash</v-icon>
                        <span class="text-caption font-weight-bold green--text text--darken-2">CONTADO</span>
                      </div>
                      <v-text-field
                        v-model.number="concepto.contadoPorcentaje"
                        label="% Máx. Descuento"
                        outlined
                        dense
                        hide-details="auto"
                        type="number"
                        min="0"
                        max="100"
                        suffix="%"
                        class="mb-1"
                        :rules="[(v) => (v !== null && v !== '' && v >= 0 && v <= 100) || 'Entre 0 y 100']" />
                      <v-sheet rounded class="pa-1 text-center green lighten-5">
                        <span class="text-caption grey--text text--darken-1">Mínimo: </span>
                        <span class="font-weight-bold green--text text--darken-2">
                          {{ calcularPrecioMinimo(concepto, concepto.contadoPorcentaje) }}
                        </span>
                      </v-sheet>
                    </v-card>
                  </v-col>

                  <!-- Crédito -->
                  <v-col cols="12" md="6">
                    <v-card outlined class="pa-2" :style="{ borderLeft: '3px solid #1976D2' }">
                      <div class="d-flex align-center mb-2">
                        <v-icon x-small color="blue darken-2" class="mr-1">mdi-credit-card</v-icon>
                        <span class="text-caption font-weight-bold blue--text text--darken-2">CRÉDITO</span>
                      </div>
                      <v-text-field
                        v-model.number="concepto.creditoPorcentaje"
                        label="% Máx. Descuento"
                        outlined
                        dense
                        hide-details="auto"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        suffix="%"
                        class="mb-1"
                        :rules="[(v) => (v !== null && v !== '' && v >= 0 && v <= 100) || 'Entre 0 y 100']" />
                      <v-sheet rounded class="pa-1 text-center blue lighten-5">
                        <span class="text-caption grey--text text--darken-1">Mínimo: </span>
                        <span class="font-weight-bold blue--text text--darken-2">
                          {{ calcularPrecioMinimo(concepto, concepto.creditoPorcentaje) }}
                        </span>
                      </v-sheet>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card>
            </v-card-text>
          </v-card>

          <v-card
            v-if="form.areas.length > 0 && form.areas.some((area) => area.conceptos.length > 0)"
            outlined
            class="pa-2">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-sheet
                  rounded
                  class="pa-2 my-1 d-flex align-center justify-center green lighten-5"
                  style="height: 40px">
                  <span class="text-caption grey--text text--darken-1 mr-1">TOTAL</span>
                  <span class="text-subtitle-2 font-weight-bold green--text text--darken-2">
                    {{ calcularTotalPorModalidad('contado') }}
                  </span>
                </v-sheet>

                <v-sheet
                  rounded
                  class="pa-2 my-1 d-flex align-center justify-center green lighten-5"
                  style="height: 40px">
                  <span class="text-caption grey--text text--darken-1 mr-1">TOTAL MÍNIMO</span>
                  <span class="text-subtitle-2 font-weight-bold green--text text--darken-2">
                    {{ calcularPrecioTotalMinimoPorModalidad('contado') }}
                  </span>
                </v-sheet>
              </v-col>

              <v-col cols="12" md="6">
                <v-sheet
                  rounded
                  class="pa-2 my-1 d-flex align-center justify-center primary lighten-5"
                  style="height: 40px">
                  <span class="text-caption grey--text text--darken-1 mr-1">TOTAL</span>
                  <span class="text-subtitle-2 font-weight-bold primary--text">
                    {{ calcularTotalPorModalidad('credito') }}
                  </span>
                </v-sheet>

                <v-sheet
                  rounded
                  class="pa-2 my-1 d-flex align-center justify-center primary lighten-5"
                  style="height: 40px">
                  <span class="text-caption grey--text text--darken-1 mr-1">TOTAL MÍNIMO</span>
                  <span class="text-subtitle-2 font-weight-bold primary--text">
                    {{ calcularPrecioTotalMinimoPorModalidad('credito') }}
                  </span>
                </v-sheet>
              </v-col>
            </v-row>
          </v-card>

          <v-alert v-if="form.areas.length === 0" type="info" dense text class="mb-0">
            No hay áreas agregadas. Haga clic en "Agregar Área" para comenzar.
          </v-alert>
        </v-form>

        <v-alert v-if="mensajeError" type="error" dense text class="mt-3 mb-0">
          {{ mensajeError }}
        </v-alert>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-4 py-3">
        <v-spacer />
        <v-btn text color="grey darken-1" @click="cerrar" :disabled="enviando">Cancelar</v-btn>
        <v-btn color="primary" :loading="enviando" @click="guardar">
          <v-icon left small>{{ esEdicion ? 'mdi-content-save' : 'mdi-check' }}</v-icon>
          {{ esEdicion ? 'Actualizar' : 'Guardar' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ListaPrecioService from '../../services/lista-precio.service';
import TipoListaPrecioService from '../../services/tipo-lista-precio.service';
import CategoriaUniversidadService from '../../services/categoria-universidad.service';
import AreaUniversidadService from '../../services/area-universidad.service';
import ConceptosPagosService from '../../services/conceptos-pagos.service';

const AREAS_CON_MODALIDAD = ['INGENIERÍA', 'SALUD'];

export default {
  name: 'FormularioListaPrecio',
  props: {
    universidadId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      visible: false,
      enviando: false,
      cargandoSelects: false,
      mensajeError: '',
      form: {
        id: null,
        tipoListaPrecioId: null,
        categoriaUniversidadId: null,
        areas: [],
      },
      erroresServidor: {},
      tiposListaPrecio: [],
      categoriasUniversidad: [],
      areasUniversidad: [],
      conceptosPago: [],
      modalidades: ['teorico', 'practico'],
    };
  },
  computed: {
    esEdicion() {
      return !!this.form.id;
    },
    tiposListaPrecioConEtiqueta() {
      return this.tiposListaPrecio.map((tipo) => ({
        ...tipo,
        etiqueta: `${tipo.nombre} (${tipo.tipoEstudiante === 'extranjero' ? 'Extranjero' : 'Nacional'})`,
      }));
    },
  },
  methods: {
    conceptosPagoFiltrados(conceptos, currentIdx) {
      const usados = conceptos
        .filter((_, i) => i !== currentIdx)
        .map((c) => c.conceptoPagoId)
        .filter(Boolean);
      return this.conceptosPago.filter((cp) => !usados.includes(cp.id));
    },
    async abrir(item = null) {
      this.mensajeError = '';
      this.erroresServidor = {};

      if (item) {
        this.form = {
          id: item.id,
          tipoListaPrecioId: item.tipoListaPrecioId || item.tipoListaPrecio?.id,
          categoriaUniversidadId: item.categoriaUniversidadId || item.categoriaUniversidad?.id,
          areas: this.detallesAgrupadosAFormAreas(item.detalles || []),
        };
      } else {
        this.form = {
          id: null,
          tipoListaPrecioId: null,
          categoriaUniversidadId: null,
          areas: [],
        };
      }

      this.visible = true;
      this.cargarSelects();

      this.$nextTick(() => {
        this.$refs.formulario?.resetValidation();
      });
    },
    cerrar() {
      this.visible = false;
      this.mensajeError = '';
      this.erroresServidor = {};
    },
    // Convierte detalles planos del API a estructura agrupada por área
    detallesAgrupadosAFormAreas(detalles) {
      const areasMap = {};

      for (const det of detalles) {
        const areaId = det.areaUniversidadId || det.areaUniversidad?.id;
        if (!areasMap[areaId]) {
          areasMap[areaId] = {
            areaUniversidadId: areaId,
            modalidad: det.modalidad || 'teorico',
            conceptos: [],
          };
        }

        const creditoMp = (det.modalidadesPagos || []).find((mp) => mp.modalidadPago === 'credito');
        const contadoMp = (det.modalidadesPagos || []).find((mp) => mp.modalidadPago === 'contado');

        areasMap[areaId].conceptos.push({
          conceptoPagoId: det.conceptoPagoId || det.conceptoPago?.id,
          montoCuota: Number(det.montoCuota),
          numeroCuotas: Number(det.numeroCuotas),
          admiteCuotasVariables: Boolean(det.admiteCuotasVariables),
          creditoPorcentaje: creditoMp ? Number(creditoMp.porcentajeMaximoDescuento) : 0,
          contadoPorcentaje: contadoMp ? Number(contadoMp.porcentajeMaximoDescuento) : 0,
        });
      }

      return Object.values(areasMap);
    },
    // Convierte estructura agrupada a detalles planos para el API
    formAreasADetalles() {
      const detalles = [];

      for (const area of this.form.areas) {
        for (const concepto of area.conceptos) {
          const modalidadesPagos = [];

          modalidadesPagos.push({
            modalidadPago: 'credito',
            porcentajeMaximoDescuento: concepto.creditoPorcentaje || 0,
            precioTotalMinimo: this.calcularPrecioMinimo(concepto, concepto.creditoPorcentaje),
          });

          modalidadesPagos.push({
            modalidadPago: 'contado',
            porcentajeMaximoDescuento: concepto.contadoPorcentaje || 0,
            precioTotalMinimo: this.calcularPrecioMinimo(concepto, concepto.contadoPorcentaje),
          });

          detalles.push({
            areaUniversidadId: area.areaUniversidadId,
            conceptoPagoId: concepto.conceptoPagoId,
            montoCuota: concepto.montoCuota,
            numeroCuotas: concepto.numeroCuotas,
            admiteCuotasVariables: concepto.admiteCuotasVariables || false,
            modalidad: area.modalidad,
            modalidadesPagos,
          });
        }
      }

      return detalles;
    },
    async cargarSelects() {
      const unidadNegocioId = this.$store.state.UNIDAD_NEGOCIO_ID;
      const params1 = { universidadId: this.universidadId };
      const params2 = { unidadNegocioId };
      this.cargandoSelects = true;

      try {
        const [tiposRes, categoriasRes, areasRes, conceptosRes] = await Promise.all([
          TipoListaPrecioService.findAll({ params: params1 }),
          CategoriaUniversidadService.findAll({ params: params1 }),
          AreaUniversidadService.findAll({ params: params1 }),
          ConceptosPagosService.findAll({ params: params2 }),
        ]);

        this.tiposListaPrecio = tiposRes.data.datos || [];
        this.categoriasUniversidad = categoriasRes.data.datos || [];
        this.areasUniversidad = areasRes.data.datos || [];
        this.conceptosPago = conceptosRes.data.datos || [];
      } catch (error) {
        this.$notify({
          group: 'notifications',
          title: 'Error',
          text: 'Error al cargar datos de selección',
          type: 'error',
        });
      } finally {
        this.cargandoSelects = false;
      }
    },
    limpiarErrorServidor(campo) {
      if (this.erroresServidor[campo]) {
        this.$set(this.erroresServidor, campo, []);
      }
    },
    // eslint-disable-next-line no-unused-vars
    obtenerErroresArea(areaIdx, campo) {
      // Los errores del servidor vienen como detalles.X.campo, buscar el primer concepto de esa área
      return [];
    },
    areaNombre(area) {
      if (!area.areaUniversidadId) return '';
      const found = this.areasUniversidad.find((a) => a.id === area.areaUniversidadId);
      return found ? found.nombre.toUpperCase() : '';
    },
    areaRequiereModalidad(area) {
      const nombre = this.areaNombre(area);
      return AREAS_CON_MODALIDAD.some((a) => nombre.includes(a));
    },
    onAreaChange(area) {
      if (!this.areaRequiereModalidad(area)) {
        area.modalidad = 'teorico';
      }
    },
    agregarArea() {
      this.form.areas.push({
        areaUniversidadId: null,
        modalidad: 'teorico',
        conceptos: [],
      });
    },
    eliminarArea(index) {
      this.form.areas.splice(index, 1);
    },
    agregarConcepto(area) {
      area.conceptos.push({
        conceptoPagoId: null,
        montoCuota: 0,
        numeroCuotas: 1,
        admiteCuotasVariables: false,
        creditoPorcentaje: 0,
        contadoPorcentaje: 0,
      });
    },
    eliminarConcepto(area, index) {
      area.conceptos.splice(index, 1);
    },
    calcularPrecioTotal(concepto) {
      return Math.round((concepto.montoCuota || 0) * (concepto.numeroCuotas || 0) * 100) / 100;
    },
    calcularPrecioMinimo(concepto, porcentaje) {
      const total = this.calcularPrecioTotal(concepto);
      if (!porcentaje || porcentaje <= 0) return total;
      return Math.round((total - (total * porcentaje) / 100) * 100) / 100;
    },
    calcularTotalPorModalidad(modalidad) {
      let total = 0;

      for (const area of this.form.areas) {
        for (const concepto of area.conceptos) {
          const porcentaje = modalidad === 'credito' ? concepto.creditoPorcentaje : concepto.contadoPorcentaje;
          if (porcentaje < 100) {
            total += this.calcularPrecioTotal(concepto);
          }
        }
      }

      return Math.round(total * 100) / 100;
    },
    calcularPrecioTotalMinimoPorModalidad(modalidad) {
      let total = 0;

      for (const area of this.form.areas) {
        for (const concepto of area.conceptos) {
          const porcentaje = modalidad === 'credito' ? concepto.creditoPorcentaje : concepto.contadoPorcentaje;

          if (porcentaje < 100) {
            total += this.calcularPrecioMinimo(concepto, porcentaje);
          }
        }
      }

      return Math.round(total * 100) / 100;
    },
    async guardar() {
      if (!this.$refs.formulario.validate()) return;

      this.enviando = true;
      this.mensajeError = '';
      this.erroresServidor = {};

      try {
        const datos = {
          tipoListaPrecioId: this.form.tipoListaPrecioId,
          categoriaUniversidadId: this.form.categoriaUniversidadId,
          universidadId: this.universidadId,
          detalles: this.formAreasADetalles(),
        };

        if (this.esEdicion) {
          await ListaPrecioService.update(this.form.id, datos);
        } else {
          await ListaPrecioService.create(datos);
        }

        this.$notify({
          group: 'notifications',
          title: 'Lista de Precio',
          text: this.esEdicion ? 'Actualizado correctamente' : 'Registrado correctamente',
          type: 'success',
        });

        this.$emit('guardado');
        this.cerrar();
      } catch (error) {
        const data = error?.response?.data;
        const status = error?.response?.status;

        if (status === 422 && data?.errores) {
          this.erroresServidor = data.errores;
        }

        this.mensajeError = data?.mensaje || error.message || 'Error al guardar';
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>
